import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Menu, X } from 'lucide-react';
import LanguageSwitcher from '../LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const defaultConversion = {
  app_url: 'https://appmando.leapifyes.com/',
  app_cta_text: 'Acceder',
  demo_url: 'https://ricardoserrano-leapifyes.zohobookings.eu/#/242621000000201008',
  demo_cta_text: 'Agendar demo',
  demo_enabled: true,
};

const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [conversion, setConversion] = useState(defaultConversion);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage = location.pathname === '/';
  const isLightPage = !isHomePage; // Non-home pages have light backgrounds

  const navLinks = [
    { label: t('header.features'), to: '/funcionalidades' },
    { label: t('header.ai'), to: '/inteligencia-artificial' },
    { label: t('header.pricing'), to: '/precios' },
    { label: t('header.forWho'), to: '/para-quien' },
    { label: t('header.about'), to: '/sobre-nosotros' },
    { label: t('header.demo'), to: '/demo' },
    { label: t('header.resources'), to: '/recursos' },
    { label: t('header.blog'), to: '/blog' },
    { label: t('header.contact'), to: '/contacto' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    // Initial check
    setIsScrolled(window.scrollY > 20);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/public/conversion-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setConversion(prev => ({ ...prev, ...data })); })
      .catch(() => {});
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const scrollToTop = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  // Header style: transparent on home when not scrolled, solid bg otherwise
  const headerBg = isScrolled || isLightPage
    ? 'bg-slate-950/98 backdrop-blur-lg shadow-lg shadow-slate-900/20'
    : 'bg-transparent';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={scrollToTop}
            className="flex items-center cursor-pointer hover:opacity-90 transition-opacity"
            aria-label="Ir al inicio"
            data-testid="header-logo-btn"
          >
            <img
              src="https://res.cloudinary.com/dnjsjo6yx/image/upload/w_80,h_80,c_fill,f_webp,q_auto/v1770925218/MANDO_by_Leapifyes_tajoao"
              alt="MANDO by Leapifyes"
              className="h-10 w-auto"
              width={40}
              height={40}
              fetchPriority="high"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-2.5 py-2 rounded-lg text-xs font-medium transition-colors duration-200 whitespace-nowrap ${
                    isActive
                      ? 'text-white bg-white/10'
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden xl:flex items-center gap-2">
            <LanguageSwitcher variant="minimal" />
            <Button
              variant="ghost"
              onClick={() => window.open(conversion.app_url, '_blank')}
              className="text-slate-300 hover:text-white hover:bg-slate-800 text-xs px-3"
              data-testid="header-login-btn"
            >
              {t('header.login')}
            </Button>
            {conversion.demo_enabled !== false && (
              <Button
                onClick={() => window.open(conversion.demo_url, '_blank')}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 text-xs"
                data-testid="header-demo-btn"
              >
                {t('header.bookDemo')}
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden p-2 text-slate-300 hover:text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menú"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="xl:hidden py-4 border-t border-slate-800 bg-slate-950/98">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.to
                      ? 'text-white bg-slate-800'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4 px-4">
                <LanguageSwitcher />
                <Button
                  variant="outline"
                  onClick={() => window.open(conversion.app_url, '_blank')}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 w-full"
                  data-testid="mobile-login-btn"
                >
                  {t('header.login')}
                </Button>
                {conversion.demo_enabled !== false && (
                  <Button
                    onClick={() => window.open(conversion.demo_url, '_blank')}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold w-full"
                    data-testid="mobile-demo-btn"
                  >
                    {t('header.bookDemo')}
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
